// src/pages/app/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import "./Dashboard.css";
import useUserRole, { isSuperRole } from "../../hooks/useUserRole";
import Timecard from "./Timecard";
import ProfileModal from "./ProfileModal";

import {
  canEmployeeRequestShift,
} from "../../utils/scheduleEmployeeUtils";

import {
  auth,
  db,
  getOpenPunch,
  startPunch,
  endPunch,
  punchEntriesRef,
  getAllOpenShifts,
  requestShift,
  denyShiftRequest,
  awardShift,
  sortShiftRequests,
} from "../../lib/firebase";

import {
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  collection,
  addDoc,
  deleteDoc,
  setDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

function toDate(ts) {
  if (!ts) return null;
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  if (ts instanceof Date) return ts;
  return new Date(ts);
}

function formatDT(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function hoursBetween(start, end) {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return 0;
  return Math.max(0, (e.getTime() - s.getTime()) / 3600000);
}

function cleanYMD(s) {
  return typeof s === "string" ? s.trim() : "";
}

function ymdToStartDate(ymd, fallback) {
  const clean = cleanYMD(ymd);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return fallback;
  const d = new Date(`${clean}T00:00:00`);
  return Number.isNaN(d.getTime()) ? fallback : d;
}

function ymdToEndDate(ymd, fallback) {
  const clean = cleanYMD(ymd);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return fallback;
  const d = new Date(`${clean}T23:59:59`);
  return Number.isNaN(d.getTime()) ? fallback : d;
}

function dateToDocId(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function prettyDateFromIso(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function timeToMinutes(t) {
  if (!t || typeof t !== "string" || !/^\d{2}:\d{2}$/.test(t)) return null;
  const [hh, mm] = t.split(":").map(Number);
  return hh * 60 + mm;
}

function makeShiftRequestKey(dateId, roleKey, start, end) {
  return `${dateId || ""}_${roleKey || ""}_${start || ""}_${end || ""}`;
}

function sortTimeOffRequests(list) {
  return [...(list || [])].sort((a, b) => {
    const aApproved = a.status === "approved";
    const bApproved = b.status === "approved";

    if (aApproved !== bApproved) return aApproved ? 1 : -1;

    const aTs =
      toDate(a.createdAt)?.getTime() ||
      Number(a.createdAtMs || 0) ||
      toDate(a.updatedAt)?.getTime() ||
      0;

    const bTs =
      toDate(b.createdAt)?.getTime() ||
      Number(b.createdAtMs || 0) ||
      toDate(b.updatedAt)?.getTime() ||
      0;

    return bTs - aTs;
  });
}

function clonePlain(v) {
  return JSON.parse(JSON.stringify(v || {}));
}

export default function Dashboard() {
  const nav = useNavigate();
  const location = useLocation();
  const { role } = useUserRole();

  const [uid, setUid] = useState(auth?.currentUser?.uid || null);
  const [, setLoading] = useState(true);

  const [me, setMe] = useState({
    displayName: auth?.currentUser?.displayName || "Dashboard User",
    email: auth?.currentUser?.email || "",
    position: "—",
    payRate: null,
    photoURL: "",
    role: "FieldUser",
  });

  const [upcoming, setUpcoming] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [scheduledHours, setScheduledHours] = useState(0);
  const [workedHours, setWorkedHours] = useState(0);

  const [punchState, setPunchState] = useState("OUT");
  const [lastPunchTs, setLastPunchTs] = useState(null);
  const [punchBusy, setPunchBusy] = useState(false);

  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  useEffect(() => {
  const params = new URLSearchParams(location.search);

  if (params.get("quicklinks") === "true") {
    setQuickLinksOpen(true);
  }
}, [location.search]);
  const [timecardOpen, setTimecardOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [payConfig, setPayConfig] = useState(null);

  const [schedulePublished, setSchedulePublished] = useState(false);
  const [scheduleStatusLoading, setScheduleStatusLoading] = useState(true);

  const [myTimeOffRequests, setMyTimeOffRequests] = useState([]);
  const [superTimeOffRequests, setSuperTimeOffRequests] = useState([]);
  const [timeOffBusyKey, setTimeOffBusyKey] = useState("");
  const [, setUsersById] = useState(new Map());

  const isSuperUser = isSuperRole(role);

  const canSeeAdminPortal =
    role === "Supervisor" ||
    role === "Manager" ||
    role === "Admin" ||
    role === "Super User" ||
    role === "Super Admin";

  const canManageOpenShifts =
    role === "Supervisor" ||
    role === "Manager" ||
    role === "Admin" ||
    role === "Super User" ||
    role === "Super Admin";

  const canViewPublishedSchedule = isSuperUser || schedulePublished;

  useEffect(() => {
    const unsub = auth.onAuthStateChanged?.((u) => {
      setUid(u?.uid || null);
      if (u) {
        setMe((prev) => ({
          ...prev,
          displayName: prev.displayName || u.displayName || "Dashboard User",
          email: prev.email || u.email || "",
          photoURL: prev.photoURL || u.photoURL || "",
        }));
      }
    });

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadSchedulePublished() {
      try {
        setScheduleStatusLoading(true);
        const snap = await getDoc(doc(db, "appConfig", "general"));
        const published = snap.exists() ? !!snap.data()?.schedulePublished : false;
        if (mounted) setSchedulePublished(published);
      } catch (e) {
        console.error("Failed to load schedulePublished:", e);
        if (mounted) setSchedulePublished(false);
      } finally {
        if (mounted) setScheduleStatusLoading(false);
      }
    }

    loadSchedulePublished();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadUsersMap() {
      if (!isSuperUser) {
        if (mounted) setUsersById(new Map());
        return;
      }

      try {
        const snap = await getDocs(collection(db, "users"));
        if (!mounted) return;
        const map = new Map();
        snap.docs.forEach((d) => {
          map.set(d.id, { id: d.id, ...d.data() });
        });
        setUsersById(map);
      } catch (e) {
        console.error("Failed to load users map:", e);
        if (mounted) setUsersById(new Map());
      }
    }

    loadUsersMap();
    return () => {
      mounted = false;
    };
  }, [isSuperUser]);

  async function loadTimeOffRequests(currentUid = uid) {
    try {
      if (!currentUid) {
        setMyTimeOffRequests([]);
        if (isSuperUser) setSuperTimeOffRequests([]);
        return;
      }

      if (isSuperUser) {
        const snap = await getDocs(collection(db, "timeOffRequests"));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMyTimeOffRequests(rows.filter((r) => r.userId === currentUid));
        setSuperTimeOffRequests(sortTimeOffRequests(rows));
        return;
      }

      const myQ = query(
        collection(db, "timeOffRequests"),
        where("userId", "==", currentUid)
      );
      const snap = await getDocs(myQ);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setMyTimeOffRequests(rows);
      setSuperTimeOffRequests([]);
    } catch (e) {
      console.error("Failed to load time off requests:", e);
      setMyTimeOffRequests([]);
      if (isSuperUser) setSuperTimeOffRequests([]);
    }
  }

  useEffect(() => {
    if (!uid) return;
    loadTimeOffRequests(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isSuperUser]);

  async function handleSignOut() {
    try {
      await signOut(auth);
      nav("/", { replace: true });
    } catch (e) {
      console.error("Sign out failed:", e);
      alert("Could not sign out. Try again.");
    }
  }

  async function handlePunchToggle() {
    if (!uid || punchBusy) return;

    setPunchBusy(true);
    try {
      if (punchState === "IN") {
        const closed = await endPunch(uid);
        if (!closed) {
          const open = await getOpenPunch(uid);
          setPunchState(open ? "IN" : "OUT");
          setLastPunchTs(open?.startTime || null);
        } else {
          setPunchState("OUT");
          setLastPunchTs(closed?.endTime || new Date());
        }
      } else {
        const opened = await startPunch(uid);
        setPunchState("IN");
        setLastPunchTs(opened?.startTime || new Date());
      }
    } catch (e) {
      console.error("Punch toggle failed:", e);
      alert(e?.code ? `${e.code}: ${e.message}` : "Punch failed. Please try again.");
    } finally {
      setPunchBusy(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        if (!uid) return;

        const userSnap = await getDoc(doc(db, "users", uid));
        let nextMe = {
  displayName: auth?.currentUser?.displayName || "Dashboard User",
  email: auth?.currentUser?.email || "",
  position: "—",
  payRate: null,
  photoURL: auth?.currentUser?.photoURL || "",
  role: "FieldUser",
};

if (userSnap.exists()) {
  nextMe = { ...nextMe, ...userSnap.data() };
  if (mounted) setMe(nextMe);
}

        try {
          const paySnap = await getDoc(doc(db, "appConfig", "payRates"));
          if (mounted) setPayConfig(paySnap.exists() ? paySnap.data() : null);
        } catch (e) {
          console.error("Failed to load payRates config:", e);
          if (mounted) setPayConfig(null);
        }

        const year = new Date().getFullYear();
        const fallbackStart = new Date(`${year}-06-01T00:00:00`);
        const fallbackEnd = new Date(`${year}-06-30T23:59:59`);
        let tournamentStart = fallbackStart;
        let tournamentEnd = fallbackEnd;

        try {
          const cfgSnap = await getDoc(doc(db, "appConfig", String(year)));
          if (cfgSnap.exists()) {
            const cfg = cfgSnap.data();
            tournamentStart = ymdToStartDate(cfg?.tournamentStart, fallbackStart);
            tournamentEnd = ymdToEndDate(cfg?.tournamentEnd, fallbackEnd);
          }
        } catch {}

        let totalHours = 0;
        const upcomingRows = [];

        if (canViewPublishedSchedule) {
          const currentEmail = String(auth.currentUser?.email || nextMe.email || "")
            .trim()
            .toLowerCase();

          for (
            let d = new Date(tournamentStart);
            d <= tournamentEnd;
            d.setDate(d.getDate() + 1)
          ) {
            const dateId = dateToDocId(d);
            const daySnap = await getDoc(doc(db, "schedules", dateId));
            if (!daySnap.exists()) continue;

            const day = daySnap.data() || {};
            const assignmentsBag =
              day.__assignments && typeof day.__assignments === "object"
                ? day.__assignments
                : null;

            if (assignmentsBag) {
              for (const [roleKey, list] of Object.entries(assignmentsBag)) {
                if (!Array.isArray(list)) continue;

                for (const assignment of list) {
                  if (!assignment) continue;

                  const employeeId = String(assignment.employeeId || "").trim();
                  const employeeIdLower = employeeId.toLowerCase();

                  const matchesUser =
                    employeeId === String(uid) ||
                    (currentEmail && employeeIdLower === currentEmail);

                  if (!matchesUser) continue;

                  const startText = assignment.start || "06:00";
                  const endText = assignment.end || "19:00";

                  upcomingRows.push({
                    id: assignment.assignmentId || `${dateId}_${roleKey}_${uid}`,
                    assignment: roleKey,
                    roleKey,
                    assignmentId: assignment.assignmentId || "",
                    dateId,
                    startText,
                    endText,
                    start: new Date(`${dateId}T${startText}:00`),
                    end: new Date(`${dateId}T${endText}:00`),
                    location: "",
                  });

                  totalHours += Math.max(
                    0,
                    ((timeToMinutes(endText) || 0) - (timeToMinutes(startText) || 0)) / 60
                  );
                }
              }
            } else {
              for (const [assignmentName, arr] of Object.entries(day)) {
                if (!Array.isArray(arr)) continue;

                const normalized = arr.map((x) => String(x || "").trim().toLowerCase());
                const hasUser =
                  normalized.includes(String(uid).trim().toLowerCase()) ||
                  (currentEmail && normalized.includes(currentEmail));

                if (!hasUser) continue;

                upcomingRows.push({
                  id: `legacy_${dateId}_${assignmentName}_${uid}`,
                  assignment: assignmentName,
                  roleKey: assignmentName,
                  assignmentId: `legacy_${dateId}_${assignmentName}_${uid}`,
                  dateId,
                  startText: "06:00",
                  endText: "19:00",
                  start: new Date(`${dateId}T06:00:00`),
                  end: new Date(`${dateId}T19:00:00`),
                  location: "",
                });

                totalHours += 13;
              }
            }
          }
        }

        upcomingRows.sort((a, b) => {
          if (a.dateId < b.dateId) return -1;
          if (a.dateId > b.dateId) return 1;
          const aStart = timeToMinutes(a.startText) || 0;
          const bStart = timeToMinutes(b.startText) || 0;
          if (aStart !== bStart) return aStart - bStart;
          return String(a.assignment).localeCompare(String(b.assignment));
        });

        if (mounted) {
          setScheduledHours(canViewPublishedSchedule ? Number(totalHours.toFixed(2)) : 0);
          setUpcoming(canViewPublishedSchedule ? upcomingRows : []);
        }

      try {
  const shifts = await getAllOpenShifts();

  console.log("DASH getAllOpenShifts count:", shifts.length);
  console.log("DASH getAllOpenShifts shifts:", shifts);

  if (mounted) {
    if (!canManageOpenShifts) {
      const filtered = shifts.filter((shift) =>
        canEmployeeRequestShift(nextMe, shift)
      );

      console.log("DASH filtered shifts:", filtered);

      setOpenShifts(filtered);
    } else {
      setOpenShifts(shifts);
    }
  }
} catch (e) {
  console.error("Failed to load open shifts:", e);
  if (mounted) setOpenShifts([]);
}

        const yearStart = new Date(new Date().getFullYear(), 0, 1, 0, 0, 0);
        const now = new Date();

        const open = await getOpenPunch(uid);
        if (mounted) {
          setPunchState(open ? "IN" : "OUT");
          setLastPunchTs(open?.startTime || null);
        }

        const punchQ = query(
          punchEntriesRef(uid),
          where("startTime", ">=", yearStart),
          where("startTime", "<=", now),
          orderBy("startTime", "asc"),
          limit(2000)
        );
        const punchSnap = await getDocs(punchQ);
        const punchRows = punchSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        let total = 0;
        for (const p of punchRows) {
          if (p.startTime && p.endTime) total += hoursBetween(p.startTime, p.endTime);
        }
        if (open?.startTime) total += hoursBetween(open.startTime, now);

        if (mounted) setWorkedHours(Number(total.toFixed(2)));
      } catch (e) {
        console.error("Dashboard load error:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [uid, canViewPublishedSchedule, canManageOpenShifts]);

  const payDisplay = useMemo(() => {
    if (!payConfig) return "—";

    const base = payConfig?.positions?.[me.position];
    if (base === undefined || base === null) return "—";
    if (typeof base !== "number") return String(base);

    let rate = base;
    const bump = Number(payConfig?.supervisorBump || 0);

    if (String(me.role || "").toLowerCase() === "supervisor") {
      rate += bump;
    }

    return `$${rate.toFixed(2)}/hr`;
  }, [payConfig, me.position, me.role]);

  const quickLinks = useMemo(
    () => [
      { label: "What to Expect", path: "/what-to-expect" },
      { label: "Operations", path: "/operations" },
      { label: "Policies", path: "/policies" },
    ],
    []
  );

  const myRequestByShiftKey = useMemo(() => {
    const map = new Map();

    for (const req of myTimeOffRequests) {
      const key = makeShiftRequestKey(req.dateId, req.roleKey, req.start, req.end);
      map.set(key, req);
    }

    return map;
  }, [myTimeOffRequests]);

  const visibleUpcomingRows = useMemo(() => {
    const rows = upcoming.map((shift) => {
      const key = makeShiftRequestKey(
        shift.dateId,
        shift.roleKey,
        shift.startText,
        shift.endText
      );

      const req = myRequestByShiftKey.get(key) || null;
const liveShiftRequest = req && req.status === "pending" ? req : null;

return {
  ...shift,
  timeOffRequest: liveShiftRequest,
};
    });

    for (const req of myTimeOffRequests) {
      if (req.status !== "approved") continue;

      const key = makeShiftRequestKey(req.dateId, req.roleKey, req.start, req.end);

      const alreadyExists = rows.some(
        (r) => makeShiftRequestKey(r.dateId, r.roleKey, r.startText, r.endText) === key
      );

      if (alreadyExists) continue;

      rows.push({
        id: `approved_req_${req.id}`,
        assignment: req.assignment,
        roleKey: req.roleKey,
        assignmentId: req.assignmentId,
        dateId: req.dateId,
        startText: req.start,
        endText: req.end,
        start: new Date(`${req.dateId}T${req.start}:00`),
        end: new Date(`${req.dateId}T${req.end}:00`),
        location: "",
        requestOnly: true,
        timeOffRequest: req,
      });
    }

    rows.sort((a, b) => {
      if (a.dateId < b.dateId) return -1;
      if (a.dateId > b.dateId) return 1;
      const aStart = timeToMinutes(a.startText) || 0;
      const bStart = timeToMinutes(b.startText) || 0;
      if (aStart !== bStart) return aStart - bStart;
      return String(a.assignment).localeCompare(String(b.assignment));
    });

    return rows;
  }, [upcoming, myRequestByShiftKey, myTimeOffRequests]);

 function hasRequestedShift(shift) {
  return (shift?.requests || []).some(
    (r) => r.uid === uid && r.status !== "denied"
  );
} 

  async function handleRequestTimeOff(shift) {
    if (!uid || !shift) return;

    const key = makeShiftRequestKey(shift.dateId, shift.roleKey, shift.startText, shift.endText);
    const existing = myRequestByShiftKey.get(key);
    if (existing) return;

    const busyKey = `request_${shift.id}`;
    setTimeOffBusyKey(busyKey);

    try {
      await addDoc(collection(db, "timeOffRequests"), {
        userId: uid,
        userName: me.displayName || auth.currentUser?.displayName || "User",
        userEmail: me.email || auth.currentUser?.email || "",
        userPosition: me.position || "",
        assignment: shift.assignment || shift.roleKey || "Shift",
        roleKey: shift.roleKey || shift.assignment || "",
        assignmentId: shift.assignmentId || "",
        dateId: shift.dateId || "",
        start: shift.startText || "06:00",
        end: shift.endText || "19:00",
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdAtMs: Date.now(),
      });

      await loadTimeOffRequests(uid);
    } catch (e) {
      console.error("Failed to request time off:", e);
      alert("Could not submit time off request. Please try again.");
    } finally {
      setTimeOffBusyKey("");
    }
  }

  async function handleCancelTimeOffRequest(req) {
    if (!req?.id) return;

    const busyKey = `cancel_${req.id}`;
    setTimeOffBusyKey(busyKey);

    try {
      await deleteDoc(doc(db, "timeOffRequests", req.id));
      await loadTimeOffRequests(uid);
    } catch (e) {
      console.error("Failed to cancel time off request:", e);
      alert("Could not cancel time off request. Please try again.");
    } finally {
      setTimeOffBusyKey("");
    }
  }

  async function handleApproveTimeOffRequest(req) {
    if (!isSuperUser || !req?.id) return;

    const busyKey = `approve_${req.id}`;
    setTimeOffBusyKey(busyKey);

    try {
      await runTransaction(db, async (tx) => {
        const reqRef = doc(db, "timeOffRequests", req.id);
        const reqSnap = await tx.get(reqRef);
        if (!reqSnap.exists()) throw new Error("Request no longer exists.");

        const liveReq = { id: reqSnap.id, ...reqSnap.data() };
        if (liveReq.status !== "pending") {
          throw new Error("This request is no longer pending.");
        }

        const scheduleRef = doc(db, "schedules", liveReq.dateId);
        const scheduleSnap = await tx.get(scheduleRef);

        const day = scheduleSnap.exists() ? clonePlain(scheduleSnap.data()) : {};
        if (!day.__assignments || typeof day.__assignments !== "object") day.__assignments = {};
        if (!day.__availableShifts || typeof day.__availableShifts !== "object") day.__availableShifts = {};

        const roleKey = liveReq.roleKey;
        const list = Array.isArray(day.__assignments[roleKey]) ? [...day.__assignments[roleKey]] : [];

        const nextList = list.filter((a) => {
          if (!a) return false;
          if (liveReq.assignmentId && a.assignmentId === liveReq.assignmentId) return false;
          if (
            String(a.employeeId || "") === String(liveReq.userId || "") &&
            String(a.start || "") === String(liveReq.start || "") &&
            String(a.end || "") === String(liveReq.end || "")
          ) {
            return false;
          }
          return true;
        });

        day.__assignments[roleKey] = nextList;
day[roleKey] = nextList.map((a) => a.employeeId).filter(Boolean);

// Recreate the open shift after approved time off
if (!day.__availableShifts || typeof day.__availableShifts !== "object") {
  day.__availableShifts = {};
}

const existingOpenShifts = Array.isArray(day.__availableShifts[roleKey])
  ? day.__availableShifts[roleKey]
  : [];

const alreadyReopened = existingOpenShifts.some((s) => {
  return (
    String(s.start || "") === String(liveReq.start || "") &&
    String(s.end || "") === String(liveReq.end || "")
  );
});

if (!alreadyReopened) {
const shiftId = `${liveReq.dateId}_${roleKey}_${liveReq.start}_${liveReq.end}`;

day.__availableShifts[roleKey] = [
  ...existingOpenShifts,
  {
    id: shiftId,
    dateId: liveReq.dateId,
    roleKey: roleKey,
    start: liveReq.start || "06:00",
    end: liveReq.end || "19:00",
    position: liveReq.userPosition || "",
    roleLabel: liveReq.assignment || roleKey,
    assignment: liveReq.assignment || roleKey,
    isOpen: true,
    source: "timeOffApproval",
    reopenedFromTimeOff: true,
    reopenedAtMs: Date.now(),
  },
];
}

// Reopen slot if it is now empty
if (!day.__blockedSlots || typeof day.__blockedSlots !== "object") {
  day.__blockedSlots = {};
}
if (nextList.length === 0 && day.__blockedSlots[roleKey]) {
  delete day.__blockedSlots[roleKey];
}

tx.set(scheduleRef, day, { merge: true });

        tx.update(reqRef, {
          status: "approved",
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          approvedByUid: auth.currentUser?.uid || "",
          approvedByName:
            me.displayName || auth.currentUser?.displayName || auth.currentUser?.email || "Super User",
        });
      });

      try {
  const claimsQ = query(
    collection(db, "shiftClaims"),
    where("dateId", "==", req.dateId),
    where("roleKey", "==", req.roleKey),
    where("status", "==", "awarded")
  );

  const claimsSnap = await getDocs(claimsQ);

  for (const claimDoc of claimsSnap.docs) {
    const data = claimDoc.data() || {};
    const sameWindow =
      String(data.start || "") === String(req.start || "") &&
      String(data.end || "") === String(req.end || "");

   if (sameWindow) {
  await setDoc(
    claimDoc.ref,
    {
      ...data,
      status: "open",
      isOpen: true,
      awardedTo: null,
      awardedAt: null,
      requests: [],
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
  }
} catch (e) {
  console.error("Failed to clear shiftClaims after approving time off:", e);
}

      await loadTimeOffRequests(uid);
      setUpcoming((prev) => [...prev]);
      window.location.reload();
    } catch (e) {
      console.error("Failed to approve time off request:", e);
      alert(e?.message || "Could not approve request.");
    } finally {
      setTimeOffBusyKey("");
    }
  }

  function renderTimeOffButton(shift) {
    const req = shift.timeOffRequest;

    if (!req) {
      return (
        <button
          className="tmWarnBtn"
          disabled={timeOffBusyKey === `request_${shift.id}`}
          onClick={() => handleRequestTimeOff(shift)}
        >
          {timeOffBusyKey === `request_${shift.id}` ? "Submitting…" : "Request Time Off"}
        </button>
      );
    }

    if (req.status === "approved") {
      return (
        <button className="tmBtn" disabled style={{ opacity: 0.7 }}>
          Time Off Request Submitted
        </button>
      );
    }

    if (req.status === "pending") {
      return (
        <button
          className="tmWarnBtn"
          disabled={timeOffBusyKey === `cancel_${req.id}`}
          onClick={() => handleCancelTimeOffRequest(req)}
        >
          {timeOffBusyKey === `cancel_${req.id}` ? "Working…" : "Pending / Cancel Request"}
        </button>
      );
    }

    return (
      <button
        className="tmWarnBtn"
        disabled={timeOffBusyKey === `request_${shift.id}`}
        onClick={() => handleRequestTimeOff(shift)}
      >
        {timeOffBusyKey === `request_${shift.id}` ? "Submitting…" : "Request Time Off"}
      </button>
    );
  }

  return (
    <div className="tmDashWrap tmNoScrollPage">
      <div className="tmDashLayout tmNoScrollLayout">
        <aside className="tmProfile tmNoScrollProfile">
          <div className="tmAvatarBox">
            {me.photoURL ? (
              <img className="tmAvatar" src={me.photoURL} alt="Profile" />
            ) : (
              <div className="tmAvatar tmAvatar--placeholder">
                <svg viewBox="0 0 24 24" width="44" height="44" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4
                       7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4
                       c-3.2 0-9.6 1.6-9.6 4.8V22h19.2v-2.8
                       c0-3.2-6.4-4.8-9.6-4.8z"
                  />
                </svg>
              </div>
            )}
          </div>

          <div className="tmName">{me.displayName || "User"}</div>

          <div className="tmMiniRow">
            <span className="tmMiniLabel">Position</span>
            <span className="tmMiniValue">{me.position || "—"}</span>
          </div>

          <div className="tmMiniRow">
            <span className="tmMiniLabel">Pay Rate</span>
            <span className="tmMiniValue">{payDisplay}</span>
          </div>

          <div className="tmMiniRow">
            <span className="tmMiniLabel">Email</span>
            <span className="tmMiniValue">{me.email || auth.currentUser?.email || ""}</span>
          </div>

          <button className="tmPrimaryBtn" onClick={() => setProfileOpen(true)}>
            Update Profile
          </button>

          <button className="tmBtn tmQuickLinksBtn" onClick={() => setQuickLinksOpen(true)}>
            Quick Links
          </button>

          {canSeeAdminPortal && (
            <button className="tmBtn tmQuickLinksBtn" onClick={() => nav("/admin")}>
              Admin Portal
            </button>
          )}

          <button className="tmBtn tmQuickLinksBtn" onClick={handleSignOut}>
            Sign Out
          </button>

          {quickLinksOpen && (
            <div className="tmModalBackdrop" onClick={() => setQuickLinksOpen(false)}>
              <div className="tmModal" onClick={(e) => e.stopPropagation()}>
                <div className="tmModalHeader">
                  <div className="tmModalTitle">Quick Links</div>
                  <button className="tmModalClose" onClick={() => setQuickLinksOpen(false)}>
                    ✕
                  </button>
                </div>

                <div className="tmModalGrid tmModalGrid--single">
                  {quickLinks.map((l) => (
                    <button
                      key={l.path}
                      className="tmModalLink"
                      onClick={() => {
                        setQuickLinksOpen(false);
                        nav(l.path);
                      }}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </aside>
<section className="tmMain tmNoScrollMain">
  <div className="tmTiles tmTilesNoScroll">
    <div className="tmTile tmTileAccent tmTileEqual">
      <div className="tmTileLabel">Punch Status</div>
      <div className="tmTileValue">{punchState}</div>
      <div className="tmTileHint">Last: {formatDT(lastPunchTs)}</div>

      <button
        className="tmPrimaryBtn tmPrimaryBtn--compact"
        onClick={handlePunchToggle}
        disabled={punchBusy || !uid}
        style={{ marginTop: 10 }}
      >
        {punchBusy ? "Working…" : punchState === "IN" ? "Punch Out" : "Punch In"}
      </button>
    </div>

    <div className="tmTile tmTileEqual">
      <div className="tmTileLabel">Time Card</div>
      <div className="tmTileValueSmall">View</div>
      <div className="tmTileHint">Current week</div>

      <button
        className="tmBtn tmTileBtn"
        onClick={() => setTimecardOpen(true)}
        style={{ marginTop: "auto" }}
      >
        View Time Card
      </button>
    </div>

    <div className="tmTile tmTileEqual">
      <div className="tmTileLabel">Scheduled Hours</div>
      <div className="tmTileValue">
        {scheduleStatusLoading
          ? "…"
          : canViewPublishedSchedule
          ? scheduledHours.toFixed(2)
          : "—"}
      </div>
      <div className="tmTileHint">
        {canViewPublishedSchedule ? "Tournament week" : "Schedule not released yet"}
      </div>
    </div>

    <div className="tmTile tmTileEqual">
      <div className="tmTileLabel">Hours Worked</div>
      <div className="tmTileValue">{workedHours.toFixed(2)}</div>
      <div className="tmTileHint">Year to date</div>
    </div>
  </div>

  <div className="tmMidRow tmMidRowNoScroll">
    <div className="tmCard tmCardTight tmCardScroll">
      <div className="tmCardHead">
        <div>
          <div className="tmCardTitle">Upcoming Shifts</div>
          <div className="tmCardSub">Your tournament week assignments</div>
        </div>
      </div>

      <div className="tmCardBodyScroll">
        {scheduleStatusLoading ? (
          <div className="tmEmpty">Loading schedule…</div>
        ) : !canViewPublishedSchedule ? (
          <div className="tmEmpty">
            Your schedule has not been released yet. Please check back later.
          </div>
        ) : visibleUpcomingRows.length === 0 ? (
          <div className="tmEmpty">No assigned shifts yet.</div>
        ) : (
          <div className="tmList tmListTight">
            {visibleUpcomingRows.map((s) => (
              <div className="tmRow tmRowTight" key={s.id}>
                <div>
                  <div className="tmRowTitle">{s.assignment || "Shift"}</div>
                  <div className="tmRowMeta">
                    {prettyDateFromIso(s.dateId)} • {s.startText} – {s.endText}
                  </div>
                </div>

                {renderTimeOffButton(s)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    <div className="tmCard tmCardTight tmCardScroll">
      <div className="tmCardHead">
        <div>
          <div className="tmCardTitle">Open Shifts</div>
          <div className="tmCardSub">Tournament week availability</div>
        </div>
      </div>

      <div className="tmCardBodyScroll">
        {!canViewPublishedSchedule ? (
          <div className="tmEmpty">Schedule not released yet.</div>
        ) : openShifts.length === 0 ? (
          <div className="tmEmpty">None available right now.</div>
        ) : (
          <div className="tmList tmListTight">
            {openShifts.map((s) => (
  <div className="tmRow tmRowTight" key={s.id}>
    <div>
      <div className="tmRowTitle">
        {s.roleLabel || s.assignment || "Open Shift"}
      </div>
      <div className="tmRowMeta">
        {prettyDateFromIso(s.dateId)} •{" "}
        {s.assignment || s.roleLabel || "Open Shift"} •{" "}
        {s.position || "—"}
      </div>
    </div>

    {!hasRequestedShift(s) ? (
     <button
  className="tmPrimaryBtn tmPrimaryBtn--compact"
  onClick={async () => {
    if (!uid) {
      alert("You must be signed in to request a shift.");
      return;
    }

    try {
     await requestShift(s.id, {
  uid,
  name: me.displayName || auth.currentUser?.displayName || "User",
  displayName: me.displayName || auth.currentUser?.displayName || "User",
  email: me.email || auth.currentUser?.email || "",
  position: me.position || "",
  role: me.role || role || "",

  // 🔥 THESE ARE THE FIX
  dateId: s.dateId,
  roleKey: s.roleKey,
  roleLabel: s.roleLabel,
  start: s.start,
  end: s.end,

  // extras
  scheduledHoursAtRequest: scheduledHours,
  status: "pending",
});
      alert("Shift request submitted.");
      window.location.reload();
    } catch (e) {
      console.error("Failed to request shift:", e);
      alert(e?.message || "Could not request shift.");
    }
  }}
>
  Request Shift
</button>
    ) : (
      <button className="tmBtn" disabled>
        Requested
      </button>
    )}
  </div>
))}
          </div>
        )}
      </div>
    </div>
  </div>
</section>
       
        {timecardOpen && (
          <div className="tmModalBackdrop" onClick={() => setTimecardOpen(false)}>
            <div
              className="tmModal tmModal--wide"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Time Card"
            >
              <div className="tmModalHeader">
                <div className="tmModalTitle">Time Card</div>
                <button className="tmModalClose" onClick={() => setTimecardOpen(false)}>
                  ✕
                </button>
              </div>

              <div style={{ padding: 12 }}>
                <Timecard />
              </div>
            </div>
          </div>
        )}

        {profileOpen && (
          <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
        )}
      </div>
    </div>
  );
}